import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HomeIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  KeyIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const PLATFORM_TYPES = new Set(['system_admin', 'account_manager']);

/**
 * Storage key for remembering the user's most recently chosen login context.
 * Stored as JSON: { type: 'agent'|'renter'|'owner'|'system_admin'|'account_manager', id: number|'__platform__', userId: number }
 */
const LAST_CONTEXT_KEY = 'lastLoginContext';

/**
 * Persist (or read) the most-recent context choice for a user. We key by
 * userId so that switching accounts on a shared device doesn't pre-highlight
 * the wrong card.
 */
export const rememberLastContext = (userId, ctx) => {
  try {
    localStorage.setItem(
      LAST_CONTEXT_KEY,
      JSON.stringify({ userId, type: ctx.type, id: ctx.id })
    );
  } catch (_) { /* ignore quota errors */ }
};

export const readLastContext = (userId) => {
  try {
    const raw = localStorage.getItem(LAST_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.userId !== userId) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

/** Visual config per context type — palette matches the rest of the app. */
const TYPE_META = {
  system_admin: {
    label: 'System Admin',
    Icon: ShieldCheckIcon,
    badgeBg: 'bg-violet-100',
    badgeText: 'text-violet-700',
    avatarFrom: 'from-violet-600',
    avatarTo: 'to-purple-700',
    ring: 'ring-violet-500',
    subtitle: 'Full platform access — agencies, users and settings',
  },
  account_manager: {
    label: 'Account Manager',
    Icon: ShieldCheckIcon,
    badgeBg: 'bg-indigo-100',
    badgeText: 'text-indigo-700',
    avatarFrom: 'from-indigo-500',
    avatarTo: 'to-indigo-700',
    ring: 'ring-indigo-500',
    subtitle: 'Platform oversight — manage assigned agencies',
  },
  agent: {
    label: 'Agent',
    Icon: BuildingOffice2Icon,
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
    avatarFrom: 'from-emerald-500',
    avatarTo: 'to-emerald-600',
    ring: 'ring-emerald-500',
  },
  renter: {
    label: 'Renter',
    Icon: KeyIcon,
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
    avatarFrom: 'from-blue-500',
    avatarTo: 'to-indigo-600',
    ring: 'ring-blue-500',
  },
  owner: {
    label: 'Owner',
    Icon: UserGroupIcon,
    badgeBg: 'bg-purple-100',
    badgeText: 'text-purple-700',
    avatarFrom: 'from-purple-500',
    avatarTo: 'to-fuchsia-600',
    ring: 'ring-purple-500',
  },
};

/** Compute initials from agency name (or fallback label). */
const initialsFrom = (text) => {
  if (!text) return '?';
  const words = text.trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w[0]?.toUpperCase() || '').join('') || '?';
};

/** Extract the stable id for a context. Platform types use a sentinel since they have no numeric id. */
const contextId = (ctx) =>
  PLATFORM_TYPES.has(ctx.type) ? '__platform__' : (ctx.agent_id ?? ctx.renter_id ?? ctx.owner_id);

/**
 * Full-page identity selector shown after login when the user has multiple
 * agent/renter/owner identities, or when a platform user (system_admin /
 * account_manager) also has agency personas and must choose how to enter.
 *
 * Props:
 *   contexts: LoginContext[] from the backend (agency personas)
 *   platformRole: 'system_admin'|'account_manager'|null — if set, a platform card is shown first
 *   userEmail: string — to display "Signing in as ..." subtitle
 *   userId: number — used to scope the "last choice" memory
 *   onSelect: (ctx) => Promise<void> — invoked on card activation
 *   onCancel: () => void — back-to-login link
 *   loading: boolean — disables interaction while exchanging selection token
 *   error: string|null — surfaced from the parent (e.g. session expired)
 */
const UserContextSelector = ({
  contexts = [],
  platformRole = null,
  userEmail,
  userId,
  onSelect,
  onCancel,
  loading = false,
  error = null,
}) => {
  const lastChoice = useMemo(() => readLastContext(userId), [userId]);

  // Prepend a synthetic platform card when the user has a platform role.
  const allContexts = useMemo(() => {
    if (!platformRole) return contexts;
    return [{ type: platformRole }, ...contexts];
  }, [platformRole, contexts]);

  // Determine which card is initially focused — last choice if still present,
  // otherwise the first card.
  const initialIndex = useMemo(() => {
    if (lastChoice) {
      const idx = allContexts.findIndex(
        (c) => c.type === lastChoice.type && contextId(c) === lastChoice.id
      );
      if (idx >= 0) return idx;
    }
    return 0;
  }, [allContexts, lastChoice]);

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const cardRefs = useRef([]);

  // Focus the active card on mount / when activeIndex changes
  useEffect(() => {
    const el = cardRefs.current[activeIndex];
    if (el) el.focus();
  }, [activeIndex]);

  const handleKeyDown = (e, idx) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, allContexts.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(allContexts[idx]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel?.();
    }
  };

  const handleSelect = (ctx) => {
    if (loading) return;
    onSelect?.(ctx);
  };

  const isLastChoice = (ctx) => {
    if (!lastChoice) return false;
    return ctx.type === lastChoice.type && contextId(ctx) === lastChoice.id;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-blue-500/20 backdrop-blur-3xl pointer-events-none"></div>

      <div className="max-w-xl w-full space-y-8 relative">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-3 rounded-2xl shadow-lg">
              <HomeIcon className="h-12 w-12 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">Choose how to sign in</h2>
          <p className="mt-2 text-sm text-gray-600">
            Your account has multiple identities. Pick one to continue.
          </p>
          {userEmail && (
            <p className="mt-1 text-xs text-gray-500">{userEmail}</p>
          )}
        </div>

        <div className="bg-white/80 backdrop-blur-md shadow-2xl rounded-2xl border border-white/20 p-6 sm:p-8">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          <ul className="space-y-3" role="listbox" aria-label="Login identity">
            {allContexts.map((ctx, idx) => {
              const isPlatform = PLATFORM_TYPES.has(ctx.type);
              const meta = TYPE_META[ctx.type] || TYPE_META.agent;
              const Icon = meta.Icon;
              const isActive = idx === activeIndex;
              const isRecommended = isLastChoice(ctx);
              const displayTitle = isPlatform
                ? meta.label
                : (ctx.agency?.name || ctx.label);
              const initials = initialsFrom(displayTitle);

              // Render a divider before the first agency card when a platform card precedes it
              const showDivider = platformRole && idx === 1;

              return (
                <React.Fragment key={`${ctx.type}-${contextId(ctx)}-${idx}`}>
                  {showDivider && (
                    <li role="presentation">
                      <div className="flex items-center gap-3 py-1">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Agency identities</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                    </li>
                  )}
                  <li>
                    <button
                      ref={(el) => (cardRefs.current[idx] = el)}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      disabled={loading}
                      onClick={() => handleSelect(ctx)}
                      onFocus={() => setActiveIndex(idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      className={[
                        'w-full text-left group relative flex items-center gap-4 p-4 rounded-xl border transition-all duration-200',
                        'bg-white/60 hover:bg-white hover:shadow-lg hover:-translate-y-0.5',
                        'focus:outline-none focus:ring-2 focus:ring-offset-2',
                        `focus:${meta.ring}`,
                        isActive ? `border-transparent ring-2 ring-offset-2 ${meta.ring} bg-white` : 'border-gray-200',
                        loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
                      ].join(' ')}
                    >
                      {/* Avatar */}
                      <div
                        className={`flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br ${meta.avatarFrom} ${meta.avatarTo} shadow-md flex items-center justify-center text-white font-semibold tracking-wide`}
                        aria-hidden="true"
                      >
                        {(isPlatform || ctx.type === 'owner') ? (
                          <Icon className="h-6 w-6" />
                        ) : (
                          <span className="text-sm">{initials}</span>
                        )}
                      </div>

                      {/* Body */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta.badgeBg} ${meta.badgeText}`}>
                            <Icon className="h-3 w-3 mr-1" />
                            {meta.label}
                          </span>
                          {isRecommended && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              Last used
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {displayTitle}
                        </p>
                        {(meta.subtitle || ctx.subtitle || ctx.agency?.country) && (
                          <p className="text-xs text-gray-500 truncate">
                            {meta.subtitle || ctx.subtitle || ctx.agency?.country}
                          </p>
                        )}
                      </div>

                      {/* Arrow */}
                      <ArrowRightIcon className="h-5 w-5 text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </button>
                  </li>
                </React.Fragment>
              );
            })}
          </ul>

          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-emerald-600 transition-colors disabled:opacity-50"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to sign in
            </button>
          </div>

          {loading && (
            <div className="mt-4 flex items-center justify-center text-sm text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mr-2"></div>
              Signing you in...
            </div>
          )}
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500">Use ↑ / ↓ to navigate · Enter to select · Esc to cancel</p>
        </div>
      </div>
    </div>
  );
};

export default UserContextSelector;

